import { AnyAction } from 'redux';

import { SearchQueryModelConverter } from '@neptune/search-query-domain';
import { makeProjectIdentifier } from '@neptune/shared/core-project-util';
import {
  LeaderboardListGroupKey,
  LeaderboardSortingParams,
  searchLeaderboard,
  SearchLeaderboardRequest,
} from '@neptune/shared/leaderboard-domain';

import { createGroupQuery } from 'common/query';
import { createAsyncActions } from 'state/async-actions';
import { getCurrentRouteParams } from 'state/selectors-global';
import type { AppState, NThunkDispatch } from 'state/types';

export enum LeaderboardGroupPreviewActionTypes {
  closeGroup = 'LEADERBOARD_GROUP_CLOSE',
  request = 'LEADERBOARD_GROUP_PREVIEW_FETCH',
  success = 'LEADERBOARD_GROUP_PREVIEW_FETCH_SUCCESS',
  fail = 'LEADERBOARD_GROUP_PREVIEW_FETCH_FAIL',
}

const getGroupPreview = createAsyncActions({
  types: LeaderboardGroupPreviewActionTypes,
  async resolver(
    params: SearchLeaderboardRequest & {
      groupIdentifier: string;
      leaderboardIdentifier: string;
      paginationTarget?: { beforeToken: string } | { continuationToken: string };
    },
  ) {
    const { leaderboardIdentifier, groupIdentifier, paginationTarget, ...requestParams } = params;
    const result = await searchLeaderboard(requestParams);

    return {
      leaderboardIdentifier,
      groupIdentifier,
      paginationTarget,
      ...result,
    };
  },
});

const { execute: fetchGroupPreview } = getGroupPreview;

type CloseGroupPreviewParams = {
  leaderboardIdentifier: string;
  groupId: string;
};

export function closeGroupPreview({ leaderboardIdentifier, groupId }: CloseGroupPreviewParams) {
  return {
    type: LeaderboardGroupPreviewActionTypes.closeGroup,
    payload: { groupId, leaderboardIdentifier },
  } as const;
}

type OpenGroupPreviewParams = {
  currentQuery: string;
  entityId: string;
  entityType: string;
  groupId: string;
  groupKey: LeaderboardListGroupKey[];
  leaderboardIdentifier: string;
  sortOptions?: LeaderboardSortingParams;
  paginationTarget?: { beforeToken: string } | { continuationToken: string };
  types: string[];
  experimentsOnly: boolean;
  attributesToFetch: string[];
};

export const openGroupPreview = ({
  currentQuery,
  groupId,
  groupKey,
  leaderboardIdentifier,
  paginationTarget,
  sortOptions,
  types,
  experimentsOnly,
  attributesToFetch,
}: OpenGroupPreviewParams) => {
  return async (dispatch: NThunkDispatch<AnyAction>, getState: () => AppState) => {
    const { projectName, organizationName } = getCurrentRouteParams(getState());

    const groupNql = createGroupQuery(currentQuery, groupKey);

    const groupQuery = SearchQueryModelConverter.convertNqlToSearchQuery(groupNql);

    const query = SearchQueryModelConverter.convertSearchQueryToNql(groupQuery);

    const projectIdentifier = makeProjectIdentifier(organizationName, projectName);
    const grouping: SearchLeaderboardRequest['grouping'] = {
      groupBy: groupKey.map(({ field }) => ({
        name: field.name,
        aggregationMode: field.aggregation,
        type: field.type,
      })),
      openedGroups: [{ openedGroup: groupId, ...paginationTarget }],
    };

    return dispatch(
      fetchGroupPreview({
        leaderboardIdentifier,
        paginationTarget,
        type: types,
        projectIdentifier,
        groupIdentifier: groupId,
        query,
        sorting: sortOptions,
        grouping,
        experimentsOnly,
        attributesToFetch,
      }),
    );
  };
};

export type GroupActions = ReturnType<
  | typeof closeGroupPreview
  | typeof getGroupPreview.request
  | typeof getGroupPreview.success
  | typeof getGroupPreview.fail
>;
